/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useEffect, useState } from 'react'
import { useServiceContext } from '../../../../root'
import type { EosConfig, EosStudyGroup, EosStudy, EosEnvironment } from '../../../../types/eos'
import Loader from '~/components/shared/Loader'

interface EosStudyInfoProps {
  configUuids: {
    studyGroup?: string
    study?: string
    environment?: string
  }
  config: Partial<EosConfig>
  handleConfigChange?: (config: Partial<EosConfig>) => void
}

const EosStudyInfo: React.FC<EosStudyInfoProps> = ({ configUuids, config, handleConfigChange }) => {
  const context = useServiceContext()
  const { translate } = context.service.filter
  const [configData, setConfigData] = useState<Partial<EosConfig>>(config)
  const [isLoading, setIsLoading] = useState(true)

  // Sync local display state from parent config (do NOT call handleConfigChange here to avoid loop)
  useEffect(() => {
    setConfigData(config)
  }, [config])

  useEffect(() => {
    if (configUuids.studyGroup) {
      let tempConfigData: Partial<EosConfig> = {}

      context.service.eosService!.getStudyGroups().then(
        (data: EosStudyGroup[]) => {
          setIsLoading(false)
          const studyGroupData = data.find((sg) => sg.uuid === configUuids.studyGroup)
          tempConfigData = { ...config, studyGroup: studyGroupData ?? null }
          setConfigData((prev) => ({ ...prev, studyGroup: studyGroupData ?? null }))

          const needsStudyFetch =
            configUuids.study &&
            tempConfigData.studyGroup &&
            (!tempConfigData.study || tempConfigData.study.uuid !== configUuids.study)

          const studyPromise = needsStudyFetch
            ? context.api.eos
                .getStudyGroup(tempConfigData.studyGroup!.uuid, !!tempConfigData.studyGroup!.mccEnabled)
                .then((sgData: any) => {
                  const elements = sgData?.plain ? sgData.plain().elements : (sgData?.elements ?? [])
                  const studyData = elements.find((s: EosStudy) => s.uuid === configUuids.study) ?? null
                  tempConfigData = { ...tempConfigData, study: studyData }
                  return studyData
                })
            : Promise.resolve(null).then(() => {
                tempConfigData = { ...tempConfigData, study: null }
                return null
              })

          const envPromise = needsStudyFetch
            ? (() => {
                const studyUuid = configUuids.study ?? tempConfigData.study?.uuid
                if (studyUuid) {
                  return context.api.eos.getPlinthStudyEnvironments(studyUuid).then((envData: any) => {
                    const envs = envData?.plain ? envData.plain() : (envData ?? [])
                    return envs.find((e: EosEnvironment) => e.uuid === configUuids.environment) ?? null
                  })
                }
                return Promise.resolve(null)
              })()
            : Promise.resolve(null)

          // Wait for all fetches, then update state once and notify parent once
          Promise.all([studyPromise, envPromise]).then(([studyData, envData]) => {
            const finalConfig: Partial<EosConfig> = {
              ...tempConfigData,
              study: studyData,
              environment: envData
            }
            setConfigData(finalConfig)
            handleConfigChange?.(finalConfig)
          })
        },
        () => {
          setIsLoading(false)
        }
      )
    } else {
      const resetConfig: Partial<EosConfig> = {
        ...config,
        studyGroup: null,
        study: null,
        environment: null
      }
      setConfigData(resetConfig)
      handleConfigChange?.(resetConfig)
    }
  }, [configUuids?.study])

  return (
    <div className="study-info-container fs12">
      <Loader blocking={isLoading}>
        <label className="mb5">{translate('EOS_SETUP_STUDY_GROUP')}</label>
        <div className="mb15">{configData?.studyGroup?.name}</div>
        <label className="mb5">{translate('EOS_SETUP_STUDY')}</label>
        <div className="mb15">{configData?.study?.name}</div>
        {configData?.studyGroup?.mccEnabled && (
          <>
            <label className="mb5">{translate('EMTF_MD_ENVIRONMENT')}</label>
            <div className="mb15">{configData?.environment?.name}</div>
          </>
        )}
      </Loader>
    </div>
  )
}

export default EosStudyInfo
